import { z } from 'zod';
import { AvailabilityFormSchema } from './Availability.schema';

export const ExperienceStepSchema = z.array(AvailabilityFormSchema.nullable()).refine(
  (experiences) => {
    const isBookingFilled = (booking: any) =>
      booking &&
      booking.startTime &&
      booking.endTime &&
      booking.experienceId &&
      booking.optionId !== undefined;

    const filledBookings = experiences.filter((booking) => isBookingFilled(booking));

    if (filledBookings.length === 0) {
      return false;
    }

    const maxExperienceIndex = experiences.reduce((maxIdx, booking, idx) => {
      return isBookingFilled(booking) ? Math.max(maxIdx, idx) : maxIdx;
    }, -1);

    for (let expIndex = 0; expIndex <= maxExperienceIndex; expIndex++) {
      const booking = experiences[expIndex];

      if (!isBookingFilled(booking)) {
        continue;
      }

      const isValid = AvailabilityFormSchema.safeParse(booking).success;
      if (!isValid) return false;
    }

    return true;
  },
  {
    message: 'Experience bookings do not meet the required criteria.',
  }
);

export type ExperienceBooking = z.infer<typeof AvailabilityFormSchema>;
export type ExperienceStep = z.infer<typeof ExperienceStepSchema>;

export const ExperienceTypeSchema = z.union([
  z.literal('spa-treatments'),
  z.literal('spa-facilities'),
  z.literal('spa-classes'),
  z.literal('spa-extras'),
]);
export type ExperienceType = z.infer<typeof ExperienceTypeSchema>;

export const PackageExperienceSchema = z.object({
  dayNumber: z.number(),
  dayNumberRange: z.array(z.number()).optional(),
  delayAfter: z.number().optional(),
  id: z.string(),
  numberOfSelectionsRequired: z.number().default(0),
  isOnSameDay: z.boolean(),
  options: z.array(
    z.object({
      itemId: z.string(),
      itemType: ExperienceTypeSchema,
      name: z.string(),
      meta: z.object({
        entityId: z.string(),
        entityType: z.literal('SpaPackageConfigurationGroupOption'),
      }),
    })
  ),
  meta: z.object({
    entityId: z.string(),
    entityType: z.literal('SpaPackageConfigurationGroup'),
  }),
});

export type PackageExperience = z.infer<typeof PackageExperienceSchema>;

export type OptionGroup = {
  count: number;
  dayNumber: number;
  dayNumberRange?: number[];
  delayAfter?: number;
  id: number;
  isIncluded: boolean;
  optionGroupId?: number | string;
  optionId: number | string;
  type: ExperienceType;
};

export type PackageExperienceOptions = PackageExperience['options'];
